import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import { generateCertificatePDF } from "./utils/certificatePdf";

import { QRCodeCanvas } from "qrcode.react";


function fmtDate(isoDate) {
  if (!isoDate) return "";
  // isoDate viene como YYYY-MM-DD
  const [y, m, d] = String(isoDate).split("-");
  if (!y || !m || !d) return String(isoDate);
  return `${d}/${m}/${y}`;
}

function calcStatus({ revoked, valid_until }) {
  if (revoked) return { key: "revocado", label: "REVOCADO" };

  const today = new Date();
  const valid = new Date(`${valid_until}T00:00:00`);
  const diffDays = Math.floor((valid - today) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return { key: "vencido", label: "VENCIDO" };
  if (diffDays <= 30) return { key: "proximo", label: "PRÓXIMO A VENCER" };
  return { key: "vigente", label: "VIGENTE" };
}

export default function VerifyCertificate() {
  const location = useLocation();

  // token = lo que venga después de /verify/
  const token = useMemo(() => {
    const raw = (location.pathname || "").replace("/verify/", "");
    return raw.split("/")[0].trim(); // por si traen /verify/token/loquesea
  }, [location.pathname]);

  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState("");
  /* Indica si el PDF se está preparando */
const [downloadingPdf, setDownloadingPdf] = useState(false);



  useEffect(() => {
    let mounted = true;

    const run = async () => {
      setLoading(true);
      setError("");
      setCertificate(null);

      if (!token) {
        if (mounted) {
          setError("Token inválido");
          setLoading(false);
        }
        return;
      }

      // ✅ Seleccionamos SOLO lo público (snapshots + razón social)
const { data, error } = await supabase
  .from("certificates")
  .select(`
    folio,
    token,
    serial_number,
    issue_date,
    valid_until,
    revoked,
    address_key,
    shipping_address_index,
    customer_snapshot,
    issued_via,
    equipment_label_snapshot,
    conformity_text_snapshot,
    scope_text_snapshot,
    client:client_id (
      razon_social,
      direcciones
    ),
    certifiable_product:certifiable_product_id (
      producto:producto_id (
        imagen
      )
    )
  `)
  .eq("token", token)
  .single();

console.log("CERTIFICADO:", data);
console.log("ERROR SUPABASE:", error);
      if (!mounted) return;

      if (error || !data) {
        setError("Certificado no válido");
        setLoading(false);
        return;
      }

      setCertificate(data);
      setLoading(false);
    };

    run();

    return () => {
      mounted = false;
    };
  }, [token]);

  const status = useMemo(() => {
    if (!certificate) return null;
    return calcStatus({
      revoked: certificate.revoked,
      valid_until: certificate.valid_until,
    });
  }, [certificate]);

  
const sucursalAlias = useMemo(() => {
  const envios = certificate?.client?.direcciones?.envio;

  if (!Array.isArray(envios) || envios.length === 0) return null;

  // 1) usar address_key si existe
  if (
    certificate?.address_key !== null &&
    certificate?.address_key !== undefined &&
    certificate?.address_key !== ""
  ) {
    const index = Number(certificate.address_key);
    if (!Number.isNaN(index)) {
      return envios[index]?.alias || null;
    }
  }

  // 2) fallback a shipping_address_index si existe
  if (
    certificate?.shipping_address_index !== null &&
    certificate?.shipping_address_index !== undefined
  ) {
    return envios[certificate.shipping_address_index]?.alias || null;
  }

  // 3) último fallback
  return envios[0]?.alias || null;
}, [certificate]);


/* =========================================================
   DATOS DEL CLIENTE

   Certificados normales:
   toman información de la tabla clients.

   Certificados activados públicamente:
   toman la información de customer_snapshot.
========================================================= */

const customerName =
  certificate?.client?.razon_social ||
  certificate?.customer_snapshot?.name ||
  null;

const customerBranch =
  sucursalAlias ||
  certificate?.customer_snapshot?.branch ||
  null;

const customerAddress =
  certificate?.customer_snapshot?.address || {};

const customerAddressText = [
  customerAddress.street,
  customerAddress.neighborhood,
  customerAddress.city,
  customerAddress.state,
  customerAddress.postal_code,
  customerAddress.country,
]
  .filter(Boolean)
  .join(", ");

  /* =========================================================
   DESCARGAR CERTIFICADO PDF

   Traemos solamente los datos necesarios para generar
   el PDF completo.

   Funciona con:
   - certificados normales creados en el cotizador
   - certificados activados por el comprador
========================================================= */
const handleDownloadPDF = async () => {

  if (!certificate?.token) return;

  setDownloadingPdf(true);

  try {

    /* =====================================================
       1. Traer certificado + equipo + producto.
    ===================================================== */
    const {
      data: fullCertificate,
      error: certificateError,
    } = await supabase
      .from("certificates")
      .select(`
        folio,
        token,
        serial_number,
        issue_date,
        valid_until,
        status,
        revoked,
        address_key,
        shipping_address_index,
        customer_snapshot,
        product_snapshot,
        equipment_label_snapshot,
        conformity_text_snapshot,
        scope_text_snapshot,

        client:client_id (
          razon_social,
          direcciones
        ),

        certifiable_product:certifiable_product_id (
          id,
          brand,
          model,
          type,
          specifications,

          producto:producto_id (
            id,
            descripcion,
            sku,
            marca,
            imagen,
            brand_logo_url
          )
        )
      `)
      .eq("token", certificate.token)
      .single();


    if (certificateError || !fullCertificate) {

      console.error(
        "Error cargando certificado para PDF:",
        certificateError
      );

      throw new Error(
        "No se pudo cargar la información del certificado."
      );
    }


    /* =====================================================
       2. Traer información pública del emisor.
    ===================================================== */
    const {
      data: companyProfile,
      error: companyError,
    } = await supabase.rpc(
      "get_public_company_profile"
    );


    if (companyError || !companyProfile) {

      console.error(
        "Error cargando empresa:",
        companyError
      );

      throw new Error(
        "No se pudieron cargar los datos del emisor."
      );
    }


    /* =====================================================
       3. Preparar CLIENTE.

       Si es un certificado normal usamos clients.

       Si fue activado públicamente construimos temporalmente
       una estructura compatible usando customer_snapshot.

       NO estamos creando ningún cliente nuevo.
    ===================================================== */

    let pdfClient = fullCertificate.client;

    let pdfCertificate = {
      ...fullCertificate,
    };


    if (!pdfClient) {

      const customer =
        fullCertificate.customer_snapshot || {};

      const customerAddress =
        customer.address || {};


      pdfClient = {

        razon_social:
          customer.name ||
          "Cliente no especificado",

        direcciones: {

          envio: [
            {

              alias:
                customer.branch || "",

              calle:
                customerAddress.street || "",

              colonia:
                customerAddress.neighborhood || "",

              ciudad:
                customerAddress.city || "",

              estado:
                customerAddress.state || "",

              cp:
                customerAddress.postal_code || "",

              pais:
                customerAddress.country || "México",

            },
          ],

        },

      };


      /*
        La estructura temporal tiene un solo domicilio,
        por eso su índice es 0.
      */
      pdfCertificate.address_key = "0";
    }


    /* =====================================================
       4. Generar y descargar PDF.
    ===================================================== */

    await generateCertificatePDF({

      certificate: pdfCertificate,

      client: pdfClient,

      address: null,

      equipment:
        fullCertificate.certifiable_product,

      companyProfile,

    });


  } catch (error) {

    console.error(
      "Error descargando certificado:",
      error
    );

    alert(
      error.message ||
      "No se pudo descargar el certificado."
    );

  } finally {

    setDownloadingPdf(false);

  }
};

  
  const statusPillClass = useMemo(() => {
    if (!status) return "bg-gray-600";
    if (status.key === "vigente") return "bg-green-600";
    if (status.key === "proximo") return "bg-yellow-600";
    if (status.key === "vencido") return "bg-orange-600";
    return "bg-red-600"; // revocado
  }, [status]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow p-6 w-full max-w-xl text-center">
          <div className="text-lg font-semibold">Verificando certificado…</div>
          <div className="text-sm text-gray-600 mt-2">Token: {token || "—"}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow p-6 w-full max-w-xl text-center">
          <div className="text-xl font-bold text-red-600">{error}</div>
          <div className="text-sm text-gray-600 mt-2">
            Si crees que es un error, solicita a tu proveedor el QR correcto.
          </div>
        </div>
      </div>
    );
  }

  // certificate existe aquí
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
      <div className="relative bg-white border-4 border-gray-200 rounded-xl shadow-xl p-10 w-full max-w-3xl">


{status?.key === "vencido" && (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
    <div className="text-red-200 text-6xl font-bold rotate-[-30deg] opacity-40">
      VENCIDO
    </div>
  </div>
)}

        
        <div className="flex flex-col items-center text-center gap-2">
          <h1 className="text-2xl font-bold">CERTIFICADO #{certificate.folio}</h1>

          {status && (
            <span className={`text-white px-4 py-2 rounded-full text-sm font-semibold ${statusPillClass}`}>
              {status.label}
            </span>
          )}

          <div className="text-sm text-gray-500 mt-2">
                Verificación digital validada mediante código QR único.
          </div>

          {/* =====================================================
              DESCARGA DEL CERTIFICADO

              Botón intencionalmente grande y visible para que
              cualquier comprador encuentre rápidamente el PDF.
          ===================================================== */}

          <button
            type="button"
            onClick={handleDownloadPDF}
            disabled={downloadingPdf}
            className="
              mt-5
              bg-blue-600
              hover:bg-blue-700
              disabled:opacity-60
              text-white
              font-bold
              text-base
              px-7
              py-3
              rounded-lg
              shadow
              w-full
              md:w-auto
            "
          >
            {downloadingPdf
              ? "Preparando certificado..."
              : "⬇ DESCARGAR CERTIFICADO PDF"}
          </button>

        </div>

        <div className="mt-6 text-center">
          <div className="font-semibold text-lg leading-snug break-words">
            {certificate.equipment_label_snapshot}
          </div>

          <div className="text-xs text-gray-500 mt-2">NÚMERO DE SERIE</div>

          <div className="font-mono text-blue-700 font-semibold">
            {certificate.serial_number}
          </div>
        </div>

       <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">

  {/* COLUMNA IZQUIERDA */}
  <div className="flex flex-col gap-4">

    <div className="border rounded-lg p-4">

  <div className="text-xs text-gray-500 mb-1">
    CLIENTE
  </div>

  <div className="font-semibold">
    {customerName || "—"}
  </div>


  {customerBranch && (
    <>
      <div className="text-xs text-gray-500 mt-3">
        SUCURSAL / ESTABLECIMIENTO
      </div>

      <div className="font-semibold">
        {customerBranch}
      </div>
    </>
  )}


  {customerAddressText && (
    <>
      <div className="text-xs text-gray-500 mt-3">
        DOMICILIO
      </div>

      <div className="text-sm">
        {customerAddressText}
      </div>
    </>
  )}

</div>

    <div className="border rounded-lg p-4 flex gap-8">

      <div>
        <div className="text-xs text-gray-500 mb-1">FECHA DE EMISIÓN</div>
        <div className="font-semibold">
          {fmtDate(certificate.issue_date)}
        </div>
      </div>

      <div>
        <div className="text-xs text-gray-500 mb-1">VIGENCIA HASTA</div>
        <div className="font-semibold">
          {fmtDate(certificate.valid_until)}
        </div>
      </div>

    </div>

  </div>


  {/* COLUMNA DERECHA (IMAGEN) */}
  <div className="border rounded-lg p-4 flex items-center justify-center min-h-[180px]">

    {certificate?.certifiable_product?.producto?.imagen && (
      <img
        src={certificate.certifiable_product.producto.imagen}
        alt="Equipo certificado"
        className="w-44 h-44 object-contain"
      />
    )}

  </div>

</div>
        <div className="mt-6">
          <div className="border rounded-lg p-4 mb-4">
            <div className="text-xs text-gray-500 mb-2">TEXTO DE CONFORMIDAD</div>
            <div className="whitespace-pre-line text-sm">
              {certificate.conformity_text_snapshot || "—"}
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-2">ALCANCE</div>
            <div className="whitespace-pre-line text-sm">
              {certificate.scope_text_snapshot || "—"}
            </div>
          </div>
        </div>

<div className="mt-10 flex flex-col items-center gap-2">
  <div className="p-4 bg-white border rounded-lg shadow-sm">
    <QRCodeCanvas
      value={`https://certificados.distribuidoradeoccidente.com/verify/${certificate.token}`}
      size={160}
      level="H"
      includeMargin={true}
    />
  </div>
  <div className="text-xs text-gray-500 text-center">
    Escanee el código QR para validar autenticidad.
  </div>
</div>


        <div className="mt-6 text-xs text-gray-500 text-center">
          Este certificado se valida mediante token único ligado al QR.
        </div>
      </div>
    </div>
  );
}
